import { useState, useEffect, useCallback } from 'react';
import { api, formatCurrency, MONTHS } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function Budgets() {
  const { user } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ category_id: '', amount: '' });
  const [saving, setSaving] = useState(false);
  const currency = user?.currency || 'COP';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        api.get(`/budgets?month=${month}&year=${year}`),
        api.get('/categories?type=expense'),
      ]);
      setBudgets(b); setCategories(c);
    } catch { toast.error('Error al cargar'); }
    setLoading(false);
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post('/budgets', { ...form, month, year });
      toast.success('Presupuesto guardado'); setShowModal(false); load();
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/budgets/${id}`); toast.success('Eliminado'); load(); }
    catch (err) { toast.error(err.message); }
  };

  const handleCopy = async () => {
    try {
      const data = await api.post('/budgets/copy', { month, year });
      toast.success(`✅ ${data.copied} presupuestos copiados`);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const totalBudget = budgets.reduce((s, b) => s + b.amount, 0);
  const totalSpent = budgets.reduce((s, b) => s + b.spent, 0);

  return (
    <div style={styles.page} className="animate-fadeIn">
      <div style={styles.topRow}>
        <div style={styles.periodRow}>
          <select className="form-select" value={month} onChange={e => setMonth(Number(e.target.value))} style={styles.sel}>
            {MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
          </select>
          <select className="form-select" value={year} onChange={e => setYear(Number(e.target.value))} style={styles.sel}>
            {[2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm({category_id:'',amount:''}); setShowModal(true); }}>
          + Nuevo
        </button>
      </div>

      {/* Overall progress */}
      {budgets.length > 0 && (
        <div className="card" style={styles.overallCard}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12 }}>
            <div>
              <p style={{ fontSize:'0.8rem', color:'var(--text-secondary)' }}>Gasto total del mes</p>
              <p style={{ fontSize:'1.5rem', fontWeight:800, fontFamily:'var(--font-display)', marginTop:4 }}>
                {formatCurrency(totalSpent, currency)}
              </p>
            </div>
            <div style={{ textAlign:'right' }}>
              <p style={{ fontSize:'0.8rem', color:'var(--text-secondary)' }}>Presupuesto total</p>
              <p style={{ fontSize:'1.5rem', fontWeight:800, fontFamily:'var(--font-display)', marginTop:4, color:'var(--text-secondary)' }}>
                {formatCurrency(totalBudget, currency)}
              </p>
            </div>
          </div>
          <div className="progress-bar" style={{ height:10 }}>
            <div className="progress-fill" style={{
              width: `${Math.min((totalSpent/totalBudget)*100, 100)}%`,
              background: totalSpent > totalBudget ? 'var(--red)' : 'var(--accent)',
            }} />
          </div>
          <p style={{ fontSize:'0.78rem', color:'var(--text-secondary)', marginTop:8 }}>
            {totalBudget > 0 ? `${Math.round((totalSpent/totalBudget)*100)}% utilizado` : '—'} ·{' '}
            {totalBudget - totalSpent > 0
              ? `${formatCurrency(totalBudget - totalSpent, currency)} disponible`
              : `${formatCurrency(totalSpent - totalBudget, currency)} excedido`}
          </p>
        </div>
      )}

      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:40 }}>
          <div className="spinner" style={{ width:32, height:32 }} />
        </div>
      ) : budgets.length === 0 ? (
        <div style={styles.empty}>
          <span style={{ fontSize: 44 }}>🎯</span>
          <p>Sin presupuestos este mes</p>
          <div style={{ display:'flex', flexDirection:'column', gap:10, alignItems:'center' }}>
            <button className="btn btn-primary btn-sm" onClick={handleCopy}>
              📋 Copiar presupuestos del mes anterior
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setForm({ category_id: '', amount: '' }); setShowModal(true); }}>
              + Crear desde cero
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {budgets.map(b => {
            const pct = b.amount > 0 ? Math.min((b.spent/b.amount)*100, 100) : 0;
            const over = b.spent > b.amount;
            return (
              <div key={b.id} className="card">
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:14 }}>
                  <div style={{
                    width:44, height:44, borderRadius:12, flexShrink:0,
                    background: b.color ? `${b.color}20` : 'var(--bg-input)',
                    display:'flex', alignItems:'center', justifyContent:'center', fontSize:22,
                  }}>
                    {b.icon || '📁'}
                  </div>
                  <div style={{ flex:1 }}>
                    <p style={{ fontWeight:600 }}>{b.category_name}</p>
                    <p style={{ fontSize:'0.78rem', color:'var(--text-secondary)', marginTop:2 }}>
                      {formatCurrency(b.spent, currency)} de {formatCurrency(b.amount, currency)}
                    </p>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <span className={`badge ${over ? 'badge-red' : pct > 80 ? 'badge-yellow' : 'badge-green'}`}>
                      {Math.round(pct)}%
                    </span>
                  </div>
                  <button className="btn btn-danger btn-icon btn-sm" onClick={() => handleDelete(b.id)}>🗑</button>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{
                    width:`${pct}%`,
                    background: over ? 'var(--red)' : pct > 80 ? 'var(--yellow)' : 'var(--green)',
                  }} />
                </div>
                {over && (
                  <p style={{ fontSize:'0.75rem', color:'var(--red)', marginTop:6 }}>
                    ⚠️ Excediste por {formatCurrency(b.spent - b.amount, currency)}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Nuevo presupuesto</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave} style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div className="form-group">
                <label className="form-label">Categoría o subcategoría</label>
                <select className="form-select" value={form.category_id}
                  onChange={e => setForm({...form, category_id:e.target.value})} required>
                  <option value="">Selecciona una categoría</option>
                  {categories.filter(c => c.type === 'expense').flatMap(parent => [
                    <option key={parent.id} value={parent.id}>{parent.icon} {parent.name}</option>,
                    ...(parent.subcategories || []).map(sub =>
                      <option key={sub.id} value={sub.id}>  └ {sub.icon} {sub.name}</option>
                    )
                  ])}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Monto límite</label>
                <input type="number" className="form-input" placeholder="0" min="1"
                  value={form.amount} onChange={e => setForm({...form, amount:e.target.value})} required />
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button type="button" className="btn btn-secondary" style={{ flex:1 }} onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ flex:2 }} disabled={saving}>
                  {saving ? <span className="spinner" /> : 'Guardar presupuesto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { display:'flex', flexDirection:'column', gap:20 },
  topRow: { display:'flex', gap:10, alignItems:'center' },
  periodRow: { display:'flex', gap:8, flex:1 },
  sel: { flex:1, padding:'10px 12px', fontSize:'0.85rem' },
  overallCard: { borderColor:'rgba(124,111,255,0.2)' },
  empty: { display:'flex', flexDirection:'column', alignItems:'center', gap:14, padding:'40px 20px', color:'var(--text-secondary)', textAlign:'center' },
};
